import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckoutStep } from "../checkoutSlice";
import { useGetCartQuery } from "../../cart/api/cartApi";
import { useGetCheckoutDataQuery } from "../api/checkoutApi";
import Icon from "../../../../common/components/Icon/Icon";

interface CheckoutProgressProps {
  currentStep: CheckoutStep;
}

const CheckoutProgress: React.FC<CheckoutProgressProps> = ({ currentStep }) => {
  const { t } = useTranslation();

  // 1. Get logic flags
  const { data: cart } = useGetCartQuery();
  const needsShipping = cart?.needsShipping ?? true;

  const { data: checkoutData } = useGetCheckoutDataQuery();
  const requiresAppointment =
    checkoutData?.appointmentData?.requiresAppointment || false;

  // 2. Memoize steps to filter dynamically
  const steps = useMemo(() => {
    let allSteps: { key: CheckoutStep; label: string; icon: string }[] = [
      {
        key: "billing",
        label: t("checkoutProgress.billing", "Billing"),
        icon: "receipt-text",
      },
      {
        key: "shipping",
        label: t("checkoutProgress.shipping", "Shipping"),
        icon: "truck",
      },
      {
        key: "appointment",
        label: t("checkoutProgress.appointment", "Appointment"),
        icon: "calendar",
      },
      {
        key: "payment",
        label: t("checkoutProgress.payment", "Payment"),
        icon: "credit-card",
      },
    ];

    // Filter out disabled steps
    if (!needsShipping) {
      allSteps = allSteps.filter((s) => s.key !== "shipping");
    }

    if (!requiresAppointment) {
      allSteps = allSteps.filter((s) => s.key !== "appointment");
    }

    return allSteps;
  }, [needsShipping, requiresAppointment, t]);

  const stepKeys = steps.map((s) => s.key);
  const currentIndex = stepKeys.indexOf(currentStep);

  return (
    <div className="checkout-progress">
      <div className="checkout-progress__track">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isActive = index === currentIndex;

          let modifierClass = "";
          if (isCompleted) modifierClass = "is-completed";
          else if (isActive) modifierClass = "is-active";

          return (
            <div
              key={step.key}
              className={`checkout-progress__step ${modifierClass}`}
            >
              <div className="checkout-progress__icon-wrapper">
                {isCompleted ? (
                  <Icon iconName="check" className="icon--stroke-thickest" />
                ) : (
                  <Icon iconName={step.icon} className="icon--stroke-thicker" />
                )}
              </div>
              <span className="checkout-progress__label">{step.label}</span>
              <div className="checkout-progress__line"></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CheckoutProgress;
